<!--
	SummaryCard: one SafeSystem dashboard article (headline stats plus a donut breakdown); render
	three inside a SummaryCardGroup.
-->
<script module lang="ts">
	type Row = Record<string, string | number>;

	/** Sums `field` over rows grouped by label, in `colours` order. */
	export function tally(
		rows: Row[],
		labelOf: (row: Row) => string,
		colours: { key: string; color: string }[],
		field = 'count'
	) {
		return colours.map(({ key, color }) => ({
			key,
			color,
			value: rows
				.filter((r) => labelOf(r) === key)
				.reduce((acc, r) => acc + (r[field] as number), 0)
		}));
	}

	/** Whole percentages summing to 100, by largest remainder; NaNs when every value is 0. */
	export function percentages(values: number[]) {
		const sum = values.reduce((a, b) => a + b, 0);
		const exact = values.map((v) => (v / sum) * 100);
		const out = exact.map((e) => Math.floor(e));
		const short = 100 - out.reduce((a, b) => a + b, 0);
		exact
			.map((e, i) => [e - out[i], i])
			.sort((a, b) => b[0] - a[0])
			.slice(0, short)
			.forEach(([, i]) => out[i]++);
		return out;
	}
</script>

<script lang="ts">
	import type { Snippet } from 'svelte';
	import DataNotAvailable from '#lib/components/DataNotAvailable.svelte';
	import LegendPatch from '#lib/components/LegendPatch.svelte';

	interface Stat {
		value: string;
		unit?: string;
		of: string;
	}
	interface Slice {
		key: string;
		color: string;
		value: number;
	}
	interface Props {
		title: string;
		blurb: string;
		available?: boolean;
		statHeading?: string;
		stats?: Stat[];
		donutHeading?: string;
		donut?: Slice[];
		children?: Snippet;
	}
	const {
		title,
		blurb,
		available = true,
		statHeading,
		stats = [],
		donutHeading,
		donut = [],
		children
	}: Props = $props();

	const slices = $derived.by(() => {
		const pct = percentages(donut.map((s) => s.value));
		let start = 0;
		return donut.map((s, i) => {
			const percentage = pct[i];
			const end = i === donut.length - 1 ? 1 : start + percentage / 100;
			const slice = {
				key: s.key,
				color: s.color,
				shareText: `${isNaN(percentage) ? 0 : percentage} %`,
				stop: `${s.color} ${start}turn ${end}turn`
			};
			start = end;
			return slice;
		});
	});

	// The radial layer punches the donut hole and rings it so a dark slice stays visible on a dark card; the trailing colour shows when there is no data.
	const gradient = $derived(
		`radial-gradient(circle at center, var(--surface-raised) calc(50% - 1px), var(--border-strong) calc(50% - 1px) 50%, transparent 50%), conic-gradient(from 30deg, ${slices.map((s) => s.stop).join(', ')}), var(--surface-sunken)`
	);
</script>

<article>
	<h2>{title}</h2>
	<p class="blurb">{blurb}</p>
	{#if !available}
		<div class="noDataMessage">
			<DataNotAvailable />
		</div>
	{:else}
		{#if statHeading}
			<h3>{statHeading}</h3>
			<ul class="stats">
				{#each stats as { value, unit, of }, i (i)}
					<li>
						<span
							><span class="digits">{value}</span>{#if unit}&nbsp;{unit}{/if}</span>
						<span>{of}</span>
					</li>
				{/each}
			</ul>
		{/if}
		{#if donutHeading}
			<h3>{donutHeading}</h3>
			<div class="donutBox">
				<div class="donut" style:background={gradient} aria-hidden="true"></div>
				<ul class="donutList">
					{#each slices as { key, shareText, color } (key)}
						<li>
							<span class="swatch"><LegendPatch {color} shape="round" size="sm" /></span>
							<strong class="percentage">{shareText}</strong>
							<span>{key}</span>
						</li>
					{/each}
				</ul>
			</div>
		{/if}
		{@render children?.()}
	{/if}
</article>

<style>
	article {
		display: flex;
		flex-direction: column;
		border: 1px solid var(--border-color);
		background-color: var(--surface-raised);
		padding: var(--space-3) var(--space-5);
		min-height: 31rem;
		color: var(--text-color);
		font-size: var(--font-size-base);

		h2 {
			margin-top: var(--space-2);
			font-weight: var(--weight-bold);
			font-size: var(--font-size-xl);
			letter-spacing: var(--tracking-caps);
			text-transform: uppercase;
		}

		.blurb {
			margin-block: var(--space-2) var(--space-5);
			color: var(--text-muted);
			font-size: var(--font-size-sm);
		}

		:global(h3) {
			margin-block: var(--space-5) var(--space-4);
			font-weight: var(--weight-bold);
			font-size: var(--font-size-xs);
			letter-spacing: var(--tracking-caps);
			text-align: center;
			text-transform: uppercase;
		}
	}

	ul {
		margin: 0;
		padding: 0;
		list-style: none;
	}

	.stats {
		display: flex;
		justify-content: space-around;
		gap: var(--space-4);
		margin-bottom: var(--space-6);

		li {
			display: flex;
			flex-direction: column;
			align-items: center;
			text-align: center;
		}
	}

	.digits {
		font-weight: var(--weight-semibold);
		font-size: var(--font-size-2xl);
	}

	.donutBox {
		display: flex;
		justify-content: space-around;
		align-items: center;
		gap: var(--space-4);
		padding: var(--space-2) var(--space-2) 0;
	}

	.donut {
		flex: 0 0 40%;
		outline: 1px solid var(--border-strong);
		border-radius: var(--radius-full);
		aspect-ratio: 1;
	}

	.donutList {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);

		li {
			display: flex;
			align-items: center;
			gap: var(--space-2);
		}
	}

	.swatch {
		display: inline-flex;
		outline: 1px solid var(--border-strong);
		border-radius: var(--radius-full);
	}

	.percentage {
		min-width: 2.5rem;
		text-align: end;
	}

	.noDataMessage {
		margin-block: auto;
		color: var(--text-muted);
		text-align: center;
	}
</style>
